import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cornerstone from 'cornerstone-core';
import dicomParser from 'dicom-parser';
import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import { Share2, X } from 'lucide-react';
import { api } from '../services/api';
import { downloadStudyArchive } from '../services/studyArchive';
import ImageCardList from './ImageCardList';
import DicomViewer from './DicomViewer';
import ImagingHeader from './ImagingHeader';
import ImagingEmptyState from './ImagingEmptyState';
import ImagingLoadingState from './ImagingLoadingState';
import ShareEditorModal from './ShareEditorModal';
import StudyListControls, { StudyListNoResults } from './StudyListControls';
import { useStudyListControls } from './useStudyListControls';
import { useSeriesHydration, withSeriesLabels } from './useSeriesHydration';
import { dedupeTags, sortStudiesByDateDesc } from '../services/imagingTags';
import './ShareSelectionBar.css';

cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;

const SHARES_TAB_ATTENTION_MS = 4500;

function ImagesPage({ toast }) {
  const navigate = useNavigate();
  const [studies, setStudies] = useState([]);
  // isLoading tracks the initial folders fetch only. It starts true so
  // the loading card paints on first render (avoiding a flash of the
  // empty state) and is flipped to false in loadUploadedFolders' finally
  // block whether the request succeeded or failed.
  const [isLoading, setIsLoading] = useState(true);
  const [sharesCount, setSharesCount] = useState(null);

  // Viewer state (mirrors UploadImages)
  const [currentStudyUID, setCurrentStudyUID] = useState('');
  const [currentSeriesKey, setCurrentSeriesKey] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const listControls = useStudyListControls(studies);

  const {
    hydrateSeriesSlices,
    hydratedSeries,
    seriesHydrationProgress,
  } = useSeriesHydration({
    studies,
    setStudies,
    downloadFile: api.downloadImagingFile,
  });

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedStudyUIDs, setSelectedStudyUIDs] = useState(() => new Set());
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sharesTabAttention, setSharesTabAttention] = useState({ active: false, token: 0 });
  const sharesTabAttentionTimeoutRef = useRef(null);

  const triggerSharesTabAttention = () => {
    if (sharesTabAttentionTimeoutRef.current) clearTimeout(sharesTabAttentionTimeoutRef.current);
    setSharesTabAttention((prev) => ({ active: true, token: prev.token + 1 }));
    sharesTabAttentionTimeoutRef.current = setTimeout(() => {
      sharesTabAttentionTimeoutRef.current = null;
      setSharesTabAttention((prev) => ({ ...prev, active: false }));
    }, SHARES_TAB_ATTENTION_MS);
  };

  useEffect(() => {
    return () => {
      if (sharesTabAttentionTimeoutRef.current) clearTimeout(sharesTabAttentionTimeoutRef.current);
    };
  }, []);

  const handleSeriesClick = async (studyKey, seriesKey, sliceIndex) => {
    try {
      const result = await hydrateSeriesSlices(studyKey, seriesKey);
      if (!result) return;

      setCurrentStudyUID(studyKey);
      setCurrentSeriesKey(seriesKey);
      setCurrentIndex(sliceIndex || 0);
      setIsViewerOpen(true);
    } catch (err) {
      console.log('handleSeriesClick error: ', err);
    }
  };

  // TODO: REVISIT — handleViewerStudyChange and handleViewerSeriesChange
  // are both unreachable. DicomViewer no longer accepts onStudyChange /
  // onSeriesChange (its study/series dropdowns were removed), but we still
  // pass them down at the bottom of this file. See the note at the top of
  // DicomViewer.js before deleting or re-wiring either one.
  const handleViewerStudyChange = async (newStudyUID) => {
    setCurrentStudyUID(newStudyUID);
    const newStudy = studies.find(s => s.studyInstanceUID === newStudyUID);
    const firstSeries = newStudy?.series?.[0];
    if (firstSeries) {
      // Make sure the new study's first series has slices before showing.
      await hydrateSeriesSlices(newStudyUID, firstSeries.seriesKey);
      setCurrentSeriesKey(firstSeries.seriesKey);
    } else {
      setCurrentSeriesKey('');
    }
    setCurrentIndex(0);
  };

  const handleViewerSeriesChange = async (newSeriesKey) => {
    await hydrateSeriesSlices(currentStudyUID, newSeriesKey);
    setCurrentSeriesKey(newSeriesKey);
    setCurrentIndex(0);
  };

  // TODO: REVISIT — audit how delete failures are handled. Today they are
  // silent end to end: there is no try/catch here, ImageCardList's
  // handleCardDelete awaits this and then animates, and ImageCard's
  // deleteImage chains .then().finally() with no .catch(). A rejected
  // delete therefore surfaces as an unhandled promise rejection, the
  // confirm modal stays open with its spinner cleared, and the user is
  // shown no error at all. Compare handleDownloadImage below, which does
  // catch and toast. Worth checking the same path for partial backend
  // failures too — DeleteImagingFolder commits the DB transaction before
  // firing S3 cleanup in a goroutine, so S3 errors are invisible by design
  // and the study can disappear from the UI while blobs remain.
  //
  // handleDeleteImage performs the delete side effect only. ImageCardList
  // plays the collapse animation and then calls onStudyRemoved below to
  // drop the study from our local state, so we intentionally do not
  // setStudies here — doing so would skip the transition.
  const handleDeleteImage = async (image) => {
    await api.deleteImagingFolder(image.studyInstanceUID);
    toast('Successfully Deleted');
  };

  const handleTagsChange = async (image, nextTags) => {
    const studyKey = image.studyInstanceUID;
    try {
      const response = await api.updateImagingTags(studyKey, nextTags);
      const savedTags = dedupeTags(response?.tags || nextTags);
      setStudies((prev) => prev.map((s) => (
        s.studyInstanceUID === studyKey ? { ...s, tags: savedTags } : s
      )));
    } catch (err) {
      console.log('handleTagsChange error: ', err);
      toast('Failed to update tags.', 'warning');
    }
  };

  // setStudyLoading mirrors the helper used by UploadImages.handleSave —
  // it patches the per-study `loading` field on the matching study so that
  // ImageCard's existing renderLoadingIndicator() can pick up the
  // ProgressBar (`image.loading.type === 'progress'`) without any new
  // wiring on the card itself.
  const setStudyLoading = (studyKey, loadingValue) => {
    setStudies(prev => prev.map(s => (
      s.studyInstanceUID === studyKey
        ? { ...s, loading: loadingValue }
        : s
    )));
  };

  // handleDownloadImage pulls every unique source file referenced by the
  // study's series down from the cloud and bundles them into a single zip
  // so the browser deposits one archive (and the user extracts one folder)
  // instead of dropping dozens of loose .dcm files at the download root.
  //
  // File access follows the same pattern as hydrateSeriesSlices above:
  // `series.fileHashes` is the canonical list of keys, and
  // `api.downloadImagingFile` returns a Blob for each key. Hashes are
  // deduped across series so a file shared between series only downloads
  // once.
  //
  // Progress is reported the same way UploadImages.handleSave does — one
  // tick per completed file plus a final tick for the zip step, surfaced
  // via the study's `loading.progress` field and rendered by ImageCard's
  // built-in ProgressBar.
  const handleDownloadImage = async (image) => {
    const studyKey = image.studyInstanceUID;
    try {
      const downloaded = await downloadStudyArchive({
        study: image,
        downloadFile: api.downloadImagingFile,
        onProgress: (progress) => setStudyLoading(studyKey, { type: 'progress', progress }),
      });
      if (!downloaded) {
        toast('No files to download.', 'warning');
        return;
      }
      toast('Download complete.');
    } catch (err) {
      console.log('handleDownloadImage error: ', err);
      toast('Failed to download study.', 'warning');
    } finally {
      setStudyLoading(studyKey, null);
    }
  };

  const loadUploadedFolders = async () => {
    try {
      const response = await api.getImagingFolders();
      console.log('studies are ', response);
      if (response?.length) {
        setStudies(sortStudiesByDateDesc(response.map(withSeriesLabels)));
      }
    } catch (err) {
      console.log('err: ', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Forward the user straight into the file picker on the upload page by
  // tagging the navigation with `?action=upload`. UploadImages reads that
  // flag on mount, triggers its file picker once, and clears the param so
  // the picker doesn't re-open on refresh or back-navigation.
  const handleEmptyStateUpload = () => {
    navigate('/images/upload?action=upload');
  };

  const loadSharesCount = async () => {
    try {
      const response = await api.getImagingShares();
      setSharesCount(Array.isArray(response) ? response.length : 0);
    } catch (err) {
      console.log('loadSharesCount error: ', err);
    }
  };

  useEffect(() => {
    loadUploadedFolders();
    loadSharesCount();
  }, []);

  const startSelecting = () => {
    setSelectedStudyUIDs(new Set());
    setIsSelecting(true);
  };

  const stopSelecting = () => {
    setIsSelecting(false);
    setSelectedStudyUIDs(new Set());
  };

  const toggleStudySelection = (image) => {
    const uid = image.studyInstanceUID;
    setSelectedStudyUIDs((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const visibleUIDs = listControls.visibleStudies.map((s) => s.studyInstanceUID);
  const allVisibleSelected = visibleUIDs.length > 0
    && visibleUIDs.every((uid) => selectedStudyUIDs.has(uid));

  const toggleSelectAllVisible = () => {
    setSelectedStudyUIDs((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleUIDs.forEach((uid) => next.delete(uid));
      } else {
        visibleUIDs.forEach((uid) => next.add(uid));
      }
      return next;
    });
  };

  const shareSingleStudy = (image) => {
    setSelectedStudyUIDs(new Set([image.studyInstanceUID]));
    setIsShareModalOpen(true);
  };

  const handleCreateShare = async ({ studyInstanceUIDs, expiresAt }) => {
    return api.createImagingShare({ studyInstanceUIDs, expiresAt });
  };

  const handleShareCreated = ({ copied }) => {
    setSharesCount((prev) => (Number.isFinite(prev) ? prev + 1 : prev));
    triggerSharesTabAttention();
    toast(copied ? 'Share created. Link copied to clipboard.' : 'Share created.');
  };

  const handleShareModalClose = () => {
    setIsShareModalOpen(false);
    stopSelecting();
  };

  const selectedCount = selectedStudyUIDs.size;
  const canShare = !isLoading && studies.length > 0;

  return (
    <div className="review-list-container" style={{ paddingTop: '0px' }}>
      <ImagingHeader
        activeTab="images"
        counts={{ images: isLoading ? null : studies.length, shares: sharesCount }}
        attentionTab={sharesTabAttention.active ? 'shares' : null}
        attentionToken={sharesTabAttention.token}
        inlineActions
        actions={canShare && !isSelecting ? (
          <button
            type="button"
            className="imaging-action-button primary"
            onClick={startSelecting}
          >
            <Share2 size={20} strokeWidth={2} />
            <span>Share</span>
          </button>
        ) : null}
      />

      {isSelecting && canShare && (
        <div className="share-selection-bar" role="region" aria-label="Share selection">
          <div className="share-selection-bar-info">
            <label className="share-selection-bar-select-all">
              <input
                type="checkbox"
                className="imaging-checkbox"
                checked={allVisibleSelected}
                onChange={toggleSelectAllVisible}
                disabled={visibleUIDs.length === 0}
              />
              <span>{allVisibleSelected ? 'Deselect all' : 'Select all'}</span>
            </label>
            <span className="share-selection-bar-count">
              {selectedCount} {selectedCount === 1 ? 'study' : 'studies'} selected
            </span>
          </div>
          <div className="share-selection-bar-actions">
            <button
              type="button"
              className="imaging-action-button outline"
              onClick={stopSelecting}
            >
              <X size={18} strokeWidth={2} />
              <span>Cancel</span>
            </button>
            <button
              type="button"
              className="imaging-action-button primary"
              onClick={() => setIsShareModalOpen(true)}
              disabled={selectedCount === 0}
            >
              <Share2 size={18} strokeWidth={2} />
              <span>Share {selectedCount > 0 ? `(${selectedCount})` : ''}</span>
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <ImagingLoadingState />
      ) : studies.length === 0 ? (
        <ImagingEmptyState
          onAction={handleEmptyStateUpload}
        />
      ) : (
        <>
          <StudyListControls
            search={listControls.search}
            onSearchChange={listControls.setSearch}
            tagOptions={listControls.tagOptions}
            selectedTagKeys={listControls.selectedTagKeys}
            onToggleTag={listControls.toggleTag}
            onClearFilters={listControls.clearFilters}
            hasActiveFilters={listControls.hasActiveFilters}
            visibleCount={listControls.visibleStudies.length}
            totalCount={studies.length}
          />
          {listControls.visibleStudies.length === 0 ? (
            <StudyListNoResults onClearFilters={listControls.clearFilters} />
          ) : (
            <ImageCardList
              studies={listControls.visibleStudies}
              onSeriesClick={handleSeriesClick}
              onSeriesHydrationPrefetch={(studyUid, seriesUid) => {
                void hydrateSeriesSlices(studyUid, seriesUid);
              }}
              hydratedSeriesLookup={hydratedSeries}
              seriesHydrationProgressBySeriesUid={seriesHydrationProgress}
              onDelete={handleDeleteImage}
              onDownload={handleDownloadImage}
              onShare={isSelecting ? undefined : shareSingleStudy}
              onTagsChange={handleTagsChange}
              confirmTagChanges
              onStudyRemoved={(uid) => {
                setStudies(prev => prev.filter(s => s.studyInstanceUID !== uid));
              }}
              selectable={isSelecting}
              selectedStudyUIDs={selectedStudyUIDs}
              onToggleSelect={toggleStudySelection}
            />
          )}
        </>
      )}

      <ShareEditorModal
        isOpen={isShareModalOpen}
        onClose={handleShareModalClose}
        mode="create"
        studies={studies}
        initialSelectedUIDs={Array.from(selectedStudyUIDs)}
        onSubmit={handleCreateShare}
        onCreated={handleShareCreated}
      />

      <DicomViewer
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        studies={studies}
        currentStudyUID={currentStudyUID}
        currentSeriesKey={currentSeriesKey}
        currentIndex={currentIndex}
        onStudyChange={handleViewerStudyChange}
        onSeriesChange={handleViewerSeriesChange}
        onIndexChange={setCurrentIndex}
      />
    </div>
  );
}

export default ImagesPage;